<template>
  <Form v-slot="{ errors }: FormSlot" ref="form" class="reservation-invoice-data">
    <div class="reservation-box margin-lg">
      <div class="header">
        <h2>Fakturačné údaje</h2>
      </div>

      <div class="row">
        <div class="col-12 col-md-9">
          <div v-if="localTravellersRef.length > 0" class="row m-b-sm">
            <div class="col-12 col-md-6 form-input">
              <label for="invoice-data-source">Predvyplniť fakturačné údaje</label>

              <select
                id="invoice-data-source"
                class="form-control"
                @change="onInvoiceDataSourceChange"
              >
                <option :value="NO_PREFILL_SOURCE">Predvyplniť z cestujúceho</option>

                <option
                  v-for="(traveller, index) in localTravellersRef"
                  :key="`invoice-data-source-option-${index}`"
                  :value="index"
                  v-text="getTravellerName(traveller, index)"
                />
              </select>
            </div>
          </div>

          <div class="row">
            <InputField
              v-model="reservationState.customer.first_name"
              :rules="validations.first_name"
              label="Meno"
              name="first_name"
              class="col-6"
              @blur="saveCustomer"
            />

            <InputField
              v-model="reservationState.customer.last_name"
              :rules="validations.last_name"
              label="Priezvisko"
              name="last_name"
              class="col-6"
              @blur="saveCustomer"
            />

            <DateField
              v-model="reservationState.customer.birthdate"
              label="Dátum narodenia"
              name="birthdate"
              :max="dateValidation"
              :rules="validations.birthdate"
              class="col-6"
            />

            <InputField
              v-model="reservationState.customer.id_card"
              :rules="validations.id_card"
              label="Číslo OP"
              name="id_card"
              class="col-6"
              @blur="saveCustomer"
            />

            <div class="relative col-6">
              <div v-if="determineVisibilityOfPreFillAddress()" class="pre-fill-address">
                <div class="heading">Použiť adresu</div>

                <div
                  v-for="(address, addressIndex) in uniqueAddresses"
                  :key="`uniqueAddress-${addressIndex}`"
                  class="address-item"
                  @mousedown="useAddress(address.split('|'))"
                  @touchstart="useAddress(address.split('|'))"
                >
                  {{ getAddressString(address.split('|')) }}
                </div>
              </div>

              <InputField
                v-model="reservationState.customer.address_street"
                :rules="validations.address_street"
                label="Ulica a číslo domu"
                name="address_street"
                @blur="saveCustomer"
                @focus="setShowPreFillAddress(true)"
              />
            </div>

            <InputField
              v-model="reservationState.customer.address_city"
              :rules="validations.address_city"
              label="Mesto"
              name="address_city"
              class="col-6"
              @blur="saveCustomer"
              @focus="setShowPreFillAddress(true)"
            />

            <InputField
              v-model="reservationState.customer.address_post_code"
              :rules="validations.address_post_code"
              label="PSČ"
              name="address_post_code"
              class="col-6"
              @blur="saveCustomer"
              @focus="setShowPreFillAddress(true)"
            />

            <div class="col-6 form-input">
              <label>Krajina</label>

              <Field
                v-slot="{ field }: FieldSlot"
                v-model="reservationState.customer.address_country"
                name="address_country"
                :rules="validations.address_country"
              >
                <select
                  v-bind="field"
                  class="form-control"
                  name="address_country"
                  @change="saveCustomer"
                >
                  <option
                    v-for="(country, code) in countries"
                    :key="`reservation-invoice-county-option-${code}`"
                    :value="code"
                    v-text="country"
                  />
                </select>

                <div v-show="errors.address_country" class="text-danger">
                  {{ errors.address_country }}
                </div>
              </Field>
            </div>

            <InputField
              v-model="reservationState.customer.email"
              :rules="validations.email"
              type="email"
              label="Email"
              name="email"
              class="col-6"
              @blur="saveCustomer"
            />

            <InputField
              v-model="reservationState.customer.phone"
              :rules="validations.phone"
              type="tel"
              label="Telefónne číslo"
              name="phone"
              class="col-6"
              @blur="saveCustomer"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="reservation-box">
      <div class="header">
        <h2>
          Druh platby

          <div>
            <small class="hide-max-desktop"
              >Ak máte kupón, môžte ho uplatniť v prehľade rezervácie, v pravej časti
              obrazovky.</small
            >

            <small class="hide-desktop"
              >Ak máte kupón, môžte ho uplatniť nižšie, pod výberom druhu platby.</small
            >
          </div>
        </h2>
      </div>

      <Radio
        v-for="(method, key) in arrayPaymentMethods"
        :key="key"
        v-model="reservationState.payment_method"
        :option="method.slug"
      >
        {{ method.name }}
        <div
          v-if="method.description && method.description.length > 0"
          class="form-input-description"
        >
          {{ method.description }}
        </div>
      </Radio>

      <div class="reservation-mobile-voucher">
        <h4>Máte kupón?</h4>

        <Voucher>
          <VoucherList :vouchers="vouchers" :show-label="false" :show-divider="false" />
        </Voucher>
      </div>

      <div v-if="!isMobile">
        <div class="row m-b">
          <div class="col-6">
            <div class="form-input m-t-sm">
              <label>Poznámka k objednávke</label>

              <textarea v-model="reservationState.note" rows="4" class="form-control" />
            </div>
          </div>
        </div>

        <Checkbox v-model="reservationState.newsletter_subscribe"
          >Prajem si odoberať výhodné ponuky zájazdov</Checkbox
        >

        <Field
          v-slot="{ field }: FieldSlot"
          v-model="reservationState.terms"
          name="terms"
          :rules="{ required: true }"
        >
          <Checkbox v-bind="field" name="terms">
            <div v-html="tacName" />
          </Checkbox>
        </Field>

        <div v-show="errors.terms" class="text-danger">{{ errors.terms }}</div>
      </div>
    </div>

    <reservation-box-controls
      back-button-text="Späť na služby"
      :continue-button-text="isMobile ? 'Zobraziť zhrnutie' : 'Objednať s povinnosťou platby'"
      @return="onReturn"
      @continue="onContinue"
    />

    <Teleport to="body">
      <Transition name="alert-floating">
        <div v-if="prefillNotice" class="alert-info alert-floating">
          {{ prefillNotice }}

          <button
            type="button"
            class="alert-close"
            aria-label="Zavrieť oznámenie"
            @click="hidePrefillNotice"
          >
            &times;
          </button>
        </div>
      </Transition>
    </Teleport>
  </Form>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import type { FormContext } from 'vee-validate';
import { Field, Form } from 'vee-validate';
import Radio from '../components/input/radio.vue';
import Checkbox from '../components/input/checkbox.vue';
import InputField from '../components/input/InputField.vue';
import DateField from '../components/input/DateField.vue';
import ReservationBoxControls from '../components/ReservationBoxControls.vue';
import Voucher from '../components/Voucher.vue';
import VoucherList from '../components/VoucherList.vue';
import { useCustomer } from '../modules/useCustomer';
import { useDevice } from '../modules/useDevice';
import { useLocalTravellers } from '../modules/useLocalTravellers';
import { pushRoute, Route } from '../router';
import { useReservation } from '../modules/useReservation';
import { useDummyData } from '../modules/useDummyData';
import type { FieldSlot, FormSlot, PaymentMethodObject, Traveller } from '../types';
import { tracking } from '../modules/analytics/analytics';
import { useReservationTracking } from '../modules/useReservationTracking';

const { dateValidation } = window.RESERVATION_STORE;

const validations = {
  first_name: { required: true },
  last_name: { required: true },
  birthdate: {
    required: true,
    before: dateValidation,
    date_format: 'YYYY-MM-DD',
  },
  id_card: { required: true },
  address_country: { is_not: '-1' },
  address_street: { required: true },
  address_city: { required: true },
  address_post_code: { required: true },
  email: { required: true, email: true },
  phone: { required: true, phone: true },
};

const { trackPageView, trackEvent } = tracking();
const { reservationState, completeReservation } = useReservation();
const { saveCustomerDataOnServer } = useCustomer();
const { localTravellersRef } = useLocalTravellers();
const { isMobile } = useDevice();
const { dummyDataState, getDummyCustomer } = useDummyData();
const { trackTripEvent } = useReservationTracking();

const form = ref<FormContext | null>(null);

const showPreFillAddress = ref(false);
const { countries, paymentMethods, tacName } = window.RESERVATION_STORE;

const arrayPaymentMethods = Object.values<PaymentMethodObject>(paymentMethods);

const vouchers = computed(() => reservationState.value.vouchers);

const uniqueAddresses = computed(() => {
  const uniqueAddresses = new Set(
    localTravellersRef.value
      .filter(
        (traveller) =>
          traveller.address_street &&
          traveller.address_street.trim().length > 0 &&
          traveller.address_post_code &&
          traveller.address_post_code.trim().length > 0 &&
          traveller.address_city &&
          traveller.address_city.trim().length > 0
      )
      .map(
        (traveller) =>
          `${traveller.address_street}|${traveller.address_post_code}|${
            traveller.address_city
          }|${traveller.address_country}`
      )
  );

  return [...uniqueAddresses];
});

// Fields shared by Traveller and Customer, which can be copied from one to the other
const COPYABLE_FIELDS = [
  'first_name',
  'last_name',
  'birthdate',
  'id_card',
  'address_street',
  'address_city',
  'address_post_code',
  'address_country',
] as const;

// Value of the placeholder option, which is never a traveller index
const NO_PREFILL_SOURCE = -1;

const prefillNotice = ref<string | null>(null);
let prefillNoticeTimeout: ReturnType<typeof setTimeout> | undefined;

const getTravellerName = (traveller: Traveller, index: number) =>
  `${traveller.first_name} ${traveller.last_name}`.trim() || `Cestujúci ${index + 1}`;

const hidePrefillNotice = () => {
  clearTimeout(prefillNoticeTimeout);

  prefillNotice.value = null;
};

const showPrefillNotice = (traveller: Traveller, index: number) => {
  hidePrefillNotice();

  prefillNotice.value = `Fakturačné údaje sme predvyplnili z cestujúceho ${getTravellerName(
    traveller,
    index
  )}. Skontrolujte ich, prípadne upravte.`;

  prefillNoticeTimeout = setTimeout(hidePrefillNotice, 8000);
};

const prefillCustomerFromTraveller = (traveller: Traveller) => {
  COPYABLE_FIELDS.forEach((field) => {
    const value = traveller[field]?.trim();

    if (value) {
      reservationState.value.customer[field] = value;
    }
  });
};

const prefillFromTraveller = (index: number) => {
  const traveller = localTravellersRef.value[index];

  if (!traveller) {
    return;
  }

  prefillCustomerFromTraveller(traveller);
  saveCustomer();
  showPrefillNotice(traveller, index);
};

const onInvoiceDataSourceChange = (event: Event) => {
  const select = event.target as HTMLSelectElement;
  const { value } = select;

  // One-shot picker - jump straight back to the placeholder option
  select.selectedIndex = 0;

  prefillFromTraveller(Number(value));
};

const onReturn = () => {
  pushRoute(Route.ServicesData);
};

const setShowPreFillAddress = (value: boolean) => {
  showPreFillAddress.value = value;
};

const onContinue = async () => {
  const response = await form.value?.validate();

  if (!response?.valid) {
    return;
  }

  trackEvent({
    event: 'add_payment_info',
    payment_type: reservationState.value.payment_method ?? '',
    value: 0,
    items: [],
  });

  if (isMobile) {
    trackTripEvent({ event: 'reservation_add_invoice_data', value: 0 });
    pushRoute(Route.Overview);
  } else {
    // Complete reservation
    trackTripEvent({ event: 'reservation_submit_reservation', value: 0 });
    completeReservation();
  }
};

const silentlyValidateCustomerValues = async () =>
  new Promise((resolve) => {
    const allPromises = Object.entries(validations).map(([key, validations]) =>
      form.value?.validateField(key, { mode: 'silent' })
    );

    Promise.all(allPromises).then((response) => {
      resolve(!response.some((item) => !item?.valid));
    });
  });

const saveCustomer = async () => {
  setShowPreFillAddress(false);

  if (!(await silentlyValidateCustomerValues())) {
    return;
  }

  saveCustomerDataOnServer(reservationState.value.customer);
};

const useAddress = (address: string[]) => {
  // eslint-disable-next-line prefer-destructuring
  reservationState.value.customer.address_street = address[0];
  // eslint-disable-next-line prefer-destructuring
  reservationState.value.customer.address_post_code = address[1];
  // eslint-disable-next-line prefer-destructuring
  reservationState.value.customer.address_city = address[2];
  // eslint-disable-next-line prefer-destructuring
  reservationState.value.customer.address_country = address[3];
};

const getAddressString = (address: string[]) =>
  `${address[0]}, ${address[1]} ${address[2]}, ${countries[address[3]]}`;

const determineVisibilityOfPreFillAddress = () =>
  uniqueAddresses.value.length > 0 && showPreFillAddress.value === true;

onMounted(() => {
  if (dummyDataState.enabled && !reservationState.value.customer.first_name) {
    reservationState.value.customer = { ...reservationState.value.customer, ...getDummyCustomer() };
  }

  trackPageView({
    screen: 'RESERVATION_INVOICE_DATA',
  });
});

onUnmounted(() => {
  clearTimeout(prefillNoticeTimeout);
});
</script>
